import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import i18n from '@/i18n';
import { cn } from '@/lib/utils';

interface Option {
  value: any;
  label: string | number;
  can_use?: boolean;
}

interface IProps {
  name: string;
  label?: string | null;
  placeholder?: string | null;
  options: (Record<string, any>[] & Option[]) | undefined;
  className?: string;
  selectClass?: string;
  labelClass?: string;
  control: any;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  notRequired?: boolean;
  withoutDescription?: boolean;
  asNumber?: boolean;
  disableSort?: boolean;
  labelProp?: string;
  itemClassName?: string | ((option: any) => string);
  emptyClassName?: string;
  disabledItem?: boolean | ((option: any) => boolean);
  onChange?: (value: string | any) => void;
}

export default function SelectControl({
  name,
  label,
  placeholder,
  options,
  control,
  description,
  className,
  selectClass,
  labelClass,
  required,
  disabled,
  notRequired,
  withoutDescription,
  asNumber,
  disableSort = false,
  labelProp = 'label',
  itemClassName,
  emptyClassName,
  disabledItem,
  onChange,
}: IProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className, 'relative')}>
          {label && (
            <FormLabel className={labelClass}>
              {label} {required && <span className={cn('text-red')}>*</span>}
            </FormLabel>
          )}
          <Select
            onValueChange={(value) => {
              const finalValue = asNumber ? Number(value) : value;
              field.onChange(finalValue);
              if (onChange) {
                onChange(finalValue);
              }
            }}
            value={field.value}
            disabled={disabled}
            onOpenChange={(open) => {
              if (disabled && open) {
                return false;
              }
            }}
          >
            <FormControl>
              <SelectTrigger
                className={cn(
                  selectClass,
                  disabled && 'opacity-50 cursor-not-allowed',
                  'rounded-lg border-gray-300 bg-white',
                  'whitespace-normal break-words',
                )}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="w-[var(--radix-select-trigger-width)]">
              <SelectGroup>
                {options?.length ? (
                  (disableSort
                    ? options
                    : [...options].sort((a, b) => {
                        const aLabel = String(a?.label);
                        const bLabel = String(b?.label);
                        return aLabel?.localeCompare(bLabel, 'uz', {
                          sensitivity: 'base',
                        });
                      })
                  ).map((item) => (
                    <SelectItem
                      key={item.value}
                      value={String(item.value)}
                      disabled={
                        typeof disabledItem === 'function'
                          ? disabledItem(item)
                          : disabledItem
                      }
                      className={cn(
                        'whitespace-normal break-words',
                        typeof itemClassName === 'function'
                          ? itemClassName(item)
                          : itemClassName,
                      )}
                    >
                      {item[labelProp]}
                    </SelectItem>
                  ))
                ) : (
                  <span className={cn('text-sm px-2', emptyClassName)}>
                    {i18n.t('no.data')}
                  </span>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
          {withoutDescription ? null : (
            <div className="absolute -bottom-[18px] right-0">
              {description && <FormDescription>{description}</FormDescription>}
              {!notRequired && <FormMessage />}
            </div>
          )}
        </FormItem>
      )}
    />
  );
}
